import moment from 'moment';
import { constants } from 'day8-ui';
import {
  BOOKING, COMPANIES, CREW, PACKAGES
} from '../constants';
import { getMaxCapacityForStandardYacht } from './ratings';
import { isUltraFriday, isUltraWeekend, numberOfNights } from './week';

const { TYW_DESTINATION } = constants;

export const isCabinBooking = booking => booking && booking.subject === BOOKING.CABIN_SUBJECT;

export const isStandardBooking = booking => booking && booking.bookingType === BOOKING.STANDARD_YACHT_TYPE;

export const isGroupBooking = booking => booking && booking.bookingType === BOOKING.GROUP_TYPE;

export const getStandardBookingRating = booking => {
  const isStandardYacht = isStandardBooking(booking);
  const isCabin = isCabinBooking(booking);
  let rating = null;
  if (isCabin) rating = booking.cabinBookingType;
  if (isStandardYacht) rating = booking.standardYachtWeek.standardYacht.package.id;
  return rating;
};

export const getBoatWeek = booking => {
  if (!booking) {
    return null;
  }
  if (booking.boatWeek) {
    return booking.boatWeek;
  }
  if (booking.parentBooking && booking.parentBooking.boatWeek) {
    return booking.parentBooking.boatWeek;
  }
  return null;
};

export const getCurrencySymbol = currency => {
  switch (currency) {
  case 'EUR':
    return '€';
  case 'USD':
    return '$';
  case 'GBP':
    return '£';
  default:
    return '';
  }
};

export const getCabinIncludedText = (destinationId, week, packageData, cabinCrew) => {
  const isGreeceBootcamp = destinationId === TYW_DESTINATION.GREECE_BOOTCAMP;
  const isThursday = +week.id === 1356;
  let cabinPart;
  let additionalText = '';
  if (cabinCrew.solos === 1 && !cabinCrew.cabins) {
    cabinPart = 'One shared spot in a cabin (we match you with another guest of the same gender)';
    if (isThursday) {
      cabinPart = 'One shared spot in a double-bed cabin (we match you with another guest of the same gender)';
    }
    additionalText = ' For a private cabin all to yourself, book a Full Cabin instead. Most yachts have double beds 140cm wide. Some are bunk beds 70cm each.';
  } else if (cabinCrew.cabins > 1 && !cabinCrew.solos) {
    cabinPart = 'Full private cabins';
  } else if (!cabinCrew.cabins && cabinCrew.solos > 1) {
    cabinPart = 'Shared spots in a cabin (one spot per guest; we match you with others of the same gender)';
  } else if (cabinCrew.cabins && cabinCrew.solos) {
    cabinPart = 'A mix of full private cabins and shared spots (shared spots are matched with another guest of the same gender)';
    if (isThursday) {
      cabinPart = 'A mix of full private cabins and shared spots in a double-bed cabin (shared spots are matched with another guest of the same gender)';
    }
  } else {
    cabinPart = 'One full private cabin';
  }

  cabinPart = cabinPart.concat(
    ` on a ${packageData.name} yacht for ${numberOfNights(week) + 1} days`
  );

  if (isUltraFriday(week, destinationId)) {
    return cabinPart.concat(
      '. Including skipper',
      [PACKAGES.CLASSIC, PACKAGES.CLASSIC_MONO].includes(packageData.id)? ' and Friday VIP Ultra tickets.': ', host, Friday VIP Ultra tickets, breakfast & lunch every day and 2 dinners.'
    );
  }
  if (isUltraWeekend(week, destinationId)) {
    return cabinPart.concat(
      '. Including skipper',
      [PACKAGES.CLASSIC, PACKAGES.CLASSIC_MONO].includes(packageData.id)? ' and VIP Ultra tickets for Saturday and Sunday.': ', host, VIP Ultra tickets for Saturday and Sunday, breakfast & lunch every day and 2 dinners.'
    );
  }
  if (isGreeceBootcamp) {
    return cabinPart.concat(
      '. Including skipper, host, food package & a specially curated meal plan for the week as well as daily workout sessions.'
    );
  }
  return cabinPart.concat(
    [PACKAGES.CLASSIC, PACKAGES.CLASSIC_MONO].includes(packageData.id)? ', including skipper.': '. Including skipper, host, breakfast & lunch every day and 2 dinners.',
    additionalText
  );
};

export const getSampleCabinImages = cabinCrew => {
  let images = [];
  if (!cabinCrew) {
    return images;
  }
  if (cabinCrew.solos > 0) {
    images.push({ src: `https://wpassets.theyachtweek.com/wp-content/uploads/2026/02/solo_example.png`, alt: 'Sample solo cabin image' });
  } else if (cabinCrew.cabins > 0) {
    // Here we can add sample images
  }
  return images;
};

export const getCapacityForBooking = booking => {
  let capacity;
  let maxGuests;
  const boatWeek = getBoatWeek(booking);
  if (boatWeek) {
    capacity = boatWeek.boat.maxPersons;
    maxGuests = Math.min((boatWeek.boat.maxPersons - 1), CREW.MAX_GUESTS);
  }
  if (isStandardBooking(booking)) {
    capacity = getMaxCapacityForStandardYacht(null, null, booking.destination.id, isCabinBooking(booking), booking.standardYachtWeek.standardYacht.package);
    maxGuests = booking.standardYachtWeek.standardYacht.maxCapacity;
  }
  return {
    capacity,
    maxGuests
  };
};

export const shouldShowFeedbackForm = (booking, currentUser) => {
  // is blacklisted?
  if (currentUser?.isBlacklisted) return false;
  // is ok booking?
  if (booking.status !== BOOKING.OK_STATUS) return false;
  // is 5 days after event end date?
  const futureDate = moment(booking.week.endDate).add('5 days');
  if (moment().isBefore(futureDate)) return false;
  // did user already submit form?
  if (booking.hasAppFeedback) return false;
  // is not TYW event
  if (booking?.week?.company?.id !== COMPANIES.DAY8) return false;
  return true;
};